import React, { useEffect, useState } from 'react';
import { hostelService, subscriptionService } from '../services/api';
import { Hostel } from '../types';
import './Dashboard.css';

const SubscriptionAdmin: React.FC = () => {
  const [hostels, setHostels] = useState<Hostel[]>([]);
  const [selectedHostel, setSelectedHostel] = useState<Hostel | null>(null);
  const [sub, setSub] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    plan: 'BASIC',
    billingCycle: 'MONTHLY',
    amount: '0',
    status: 'ACTIVE',
    maxUsers: '10',
    maxBeds: '50',
    maxProperties: '1',
  });

  const loadHostels = async () => {
    setLoading(true);
    try {
      const hs = await hostelService.getAllHostels();
      setHostels(hs);
      if (!selectedHostel && hs.length) setSelectedHostel(hs[0]);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async (hostelId: number) => {
    const data = await subscriptionService.getHostelSubscription(hostelId);
    setSub(data.subscription);
    setSelectedHostel(data.hostel);
    const s = data.subscription;
    if (s) {
      setForm({
        plan: s.plan,
        billingCycle: s.billingCycle,
        amount: String(s.amount ?? 0),
        status: s.status,
        maxUsers: String(s.maxUsers ?? 10),
        maxBeds: String(s.maxBeds ?? 50),
        maxProperties: String(s.maxProperties ?? 1),
      });
    }
  };

  useEffect(() => {
    loadHostels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedHostel?.id) loadSubscription(selectedHostel.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHostel?.id]);

  const save = async () => {
    if (!selectedHostel?.id) return;
    await subscriptionService.updateHostelSubscription(selectedHostel.id, {
      plan: form.plan,
      billingCycle: form.billingCycle,
      amount: Number(form.amount),
      status: form.status,
      maxUsers: Number(form.maxUsers),
      maxBeds: Number(form.maxBeds),
      maxProperties: Number(form.maxProperties),
    });
    await loadSubscription(selectedHostel.id);
    alert('Subscription updated');
  };

  const setHostelStatus = async (status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED') => {
    if (!selectedHostel?.id) return;
    await subscriptionService.setHostelStatus(selectedHostel.id, status);
    await loadSubscription(selectedHostel.id);
  };

  return (
    <div className="dashboard-container">
      <h1>Subscription Admin</h1>

      <div className="form-container">
        <div className="form-row">
          <div className="form-group">
            <label>Select Hostel</label>
            <select
              value={selectedHostel?.id || ''}
              onChange={(e) => {
                const id = parseInt(e.target.value);
                const h = hostels.find((x) => x.id === id) || null;
                setSelectedHostel(h);
              }}
            >
              {hostels.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name} ({h.status || 'ACTIVE'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedHostel && (
          <div className="list-container">
            <div className="list-item">
              <div className="item-header">
                <h3>{selectedHostel.name}</h3>
                <span className="badge">{selectedHostel.subscriptionPlan || 'BASIC'}</span>
              </div>
              <p>
                <strong>Status:</strong> {selectedHostel.status || 'ACTIVE'}
              </p>
              <div className="action-buttons">
                <button className="btn btn-success" onClick={() => setHostelStatus('ACTIVE')}>
                  Activate
                </button>
                <button className="btn btn-warning" onClick={() => setHostelStatus('SUSPENDED')}>
                  Suspend
                </button>
                <button className="btn btn-danger" onClick={() => setHostelStatus('INACTIVE')}>
                  Deactivate
                </button>
              </div>
            </div>
          </div>
        )}

        {sub && (
          <>
            <h2>Subscription</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Plan</label>
                <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
                  <option value="BASIC">BASIC</option>
                  <option value="PRO">PRO</option>
                  <option value="ENTERPRISE">ENTERPRISE</option>
                </select>
              </div>
              <div className="form-group">
                <label>Billing Cycle</label>
                <select
                  value={form.billingCycle}
                  onChange={(e) => setForm({ ...form, billingCycle: e.target.value })}
                >
                  <option value="MONTHLY">MONTHLY</option>
                  <option value="QUARTERLY">QUARTERLY</option>
                  <option value="ANNUAL">ANNUAL</option>
                </select>
              </div>
              <div className="form-group">
                <label>Amount</label>
                <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Subscription Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                  <option value="SUSPENDED">SUSPENDED</option>
                  <option value="CANCELLED">CANCELLED</option>
                </select>
              </div>
              <div className="form-group">
                <label>Max Users</label>
                <input value={form.maxUsers} onChange={(e) => setForm({ ...form, maxUsers: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Max Beds</label>
                <input value={form.maxBeds} onChange={(e) => setForm({ ...form, maxBeds: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Max Properties</label>
                <input
                  value={form.maxProperties}
                  onChange={(e) => setForm({ ...form, maxProperties: e.target.value })}
                />
              </div>
            </div>

            <button className="btn btn-primary" onClick={save} disabled={loading}>
              Save
            </button>
          </>
        )}

        {loading && <div className="loading-container">Loading...</div>}
      </div>
    </div>
  );
};

export default SubscriptionAdmin;

